import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const SEO_META = {
  owners: {
    title: "Propriétaires",
    description: "Vos m² dormants valent plus que vous ne pensez. Sur R-1.immo, transformez vos sous-sols, caves et parkings inexploités en revenus récurrents avec un accompagnement expert.",
  },
  operators: {
    title: "Exploitants",
    description: "Escape game, dark kitchen, studio, événementiel, cave à vin : trouvez le sous-sol compatible avec votre activité. Filtres techniques, réglementaires et par usage sur R-1.immo.",
  },
  usage: {
    title: "Quel usage pour mon sous-sol ?",
    description: "Guide complet pour évaluer le potentiel de votre sous-sol : hauteur, accès, réglementation ERP, ventilation, électricité. Découvrez les usages possibles avant de vous lancer.",
  },
  about: {
    title: "À propos",
    description: "R-1.immo est la marketplace française spécialisée dans les sous-sols atypiques. Notre mission : rendre visible ce qui est invisible et créer un marché structuré pour ces lieux oubliés.",
  },
};

const CONTENT = {
  owners: {
    label: "Propriétaires",
    title: "Vos m² invisibles valent plus que vous ne pensez.",
    lede: "Un sous-sol vide, une cave oubliée, un parking sous-utilisé. Nous transformons ces surfaces dormantes en revenus récurrents, en trouvant les exploitants qui les révèlent.",
    blocks: [
      { h: "Dépôt guidé", p: "9 étapes techniques structurées. Notre formulaire capture chaque contrainte : hauteur, accès, ventilation, ERP, réglementation. Vous n'oubliez rien." },
      { h: "Score d'exploitabilité", p: "Calculé automatiquement. Vous savez exactement ce que votre lieu vaut sur le marché, et ce qu'il pourrait valoir après travaux." },
      { h: "Validation qualité", p: "Chaque annonce est vérifiée avant publication. Vous recevez uniquement des leads sérieux et qualifiés." },
      { h: "Accompagnement", p: "Nous connaissons la réglementation ERP, les changements de destination, les autorisations copropriété. Nous vous aidons à débloquer les projets complexes." },
    ],
    cta: "/deposer",
    ctaLabel: "Proposer un R-1",
  },
  operators: {
    label: "Exploitants",
    title: "Le lieu que personne ne cherche est peut-être le vôtre.",
    lede: "Escape game, dark kitchen, studio, showroom, cave à vin, événementiel. Vous cherchez un lieu qui sort du foncier classique : nous listons les sous-sols compatibles.",
    blocks: [
      { h: "Filtres par usage", p: "Sélectionnez votre projet. Nous filtrons automatiquement selon les critères critiques : hauteur, ERP, ventilation, accès public." },
      { h: "Fiches techniques exhaustives", p: "Chaque annonce détaille l'électricité, l'accès, les issues, la réglementation. Vous savez si le lieu est faisable avant même la visite." },
      { h: "Contact direct", p: "Pas d'intermédiaire, pas de courtage. Vous parlez directement au propriétaire." },
      { h: "Alertes ciblées", p: "Enregistrez vos recherches. Nous vous alertons dès qu'un lieu compatible est publié." },
    ],
    cta: "/annonces",
    ctaLabel: "Explorer les annonces",
  },
  usage: {
    label: "Ressources",
    title: "Quel usage pour mon sous-sol ?",
    lede: "Un sous-sol n'est jamais qu'un sous-sol. Selon sa hauteur, son accès, sa réglementation, il devient studio, dark kitchen, salle de sport, showroom, cave à vin, escape game, atelier, data center. Voici comment évaluer les possibilités.",
    blocks: [
      { h: "Hauteur sous plafond", p: "Sous 1,80 m : stockage, archives, cave. Entre 1,80 et 2,20 m : dark kitchen, atelier, studio podcast. Au-dessus de 2,20 m : ERP possible, escape game, salle de sport, événementiel." },
      { h: "Accès indépendant", p: "Un accès direct depuis la rue démultiplie les usages : réception du public, livraison, horaires 24/7. Sans accès indépendant : usages restreints à l'occupant principal." },
      { h: "Réglementation ERP", p: "Un local ERP peut recevoir du public. Selon la catégorie (5 à 1) et le type (activité), les usages autorisés diffèrent. Un changement de destination peut être requis." },
      { h: "Ventilation & extraction", p: "Indispensables pour cuisine, sport, événementiel. La présence d'une extraction déjà installée fait bondir la valeur du lieu." },
      { h: "Électricité", p: "Studios photo, dark kitchens, ateliers demandent une puissance élevée (>18 kVA) et souvent du triphasé." },
    ],
    cta: "/annonces",
    ctaLabel: "Voir les annonces",
  },
  about: {
    label: "À propos",
    title: "La marketplace des lieux qu'on ne voit pas.",
    lede: "R-1.immo est née d'un constat : chaque immeuble parisien cache en moyenne 60 m² de sous-sol inexploités. Multipliez par le nombre d'immeubles, c'est un parc foncier oublié équivalent à plusieurs quartiers entiers.",
    blocks: [
      { h: "Vision", p: "Rendre visible ce qui est invisible. Créer un marché structuré pour des lieux atypiques qui n'ont jamais eu de plateforme dédiée." },
      { h: "Expertise", p: "Notre équipe combine immobilier commercial, exploitation ERP, et transformation d'espaces. Nous parlons le langage des deux côtés du marché." },
      { h: "Confiance", p: "Chaque annonce est validée. Chaque exploitant est vérifié. Nous refusons les projets flous, les propriétaires opportunistes, les intermédiaires abusifs." },
    ],
    cta: "/contact",
    ctaLabel: "Nous contacter",
  },
};

export default function Editorial({ page }) {
  const c = CONTENT[page] || CONTENT.about;
  const seo = SEO_META[page] || SEO_META.about;
  useSeo({ title: seo.title, description: seo.description });
  return (
    <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24 max-w-6xl">
      <div className="label-mono mb-4 text-amber-500">§ {c.label}</div>
      <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-4xl mb-8">{c.title}</h1>
      <p className="text-xl text-zinc-300 max-w-3xl leading-relaxed mb-16">{c.lede}</p>

      <div className="grid md:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800 mb-16">
        {c.blocks.map((b, i) => (
          <div key={b.h} className="bg-zinc-950 p-8">
            <div className="label-mono mb-3 text-amber-500">§ {String(i + 1).padStart(2, "0")}</div>
            <h3 className="font-display text-2xl mb-4">{b.h}</h3>
            <p className="text-zinc-400 leading-relaxed">{b.p}</p>
          </div>
        ))}
      </div>

      <Link to={c.cta} data-testid={`editorial-cta-${page}`} className="inline-block bg-amber-500 hover:bg-amber-400 text-black px-8 py-4 text-xs uppercase tracking-widest transition-colors">
        {c.ctaLabel} →
      </Link>
    </div>
  );
}
